import { noteEvidenceAtom, noteEvidenceNotFoundAtom } from '@/atoms/panelAtoms';
import {
  NOTE_EVIDENCE_APPLY_META,
  subscribeNoteEvidenceReapply,
} from '@/components/tiptap/shared/note-evidence-reapply';
import type { Editor } from '@tiptap/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

export const useApplyNoteEvidence = (editor: Editor | null) => {
  const noteEvidence = useAtomValue(noteEvidenceAtom);
  const setNoteEvidenceNotFound = useSetAtom(noteEvidenceNotFoundAtom);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (!editor) return;

    const shouldHighlight =
      !!noteEvidence?.isShowing && noteEvidence.highlightRanges.length > 0;

    const searchTexts = shouldHighlight
      ? noteEvidence.highlightRanges
          .map((range) => range.text?.trim())
          .filter((t): t is string => !!t)
      : [];

    const applyHighlights = (isUpdate = false) => {
      // Prevent recursive updates if we're already updating from within this function
      if (isUpdatingRef.current) return;

      const { state, view } = editor;
      if (!state?.doc) return;

      const tr = state.tr;
      const { doc } = state;
      const highlightMarkType = state.schema.marks.noteEvidenceHighlight;

      let hasMatch = false;
      let hasChanges = false;

      doc.descendants((node, pos) => {
        // 1. Cleanup existing marks
        if (
          highlightMarkType &&
          node.marks.some((mark) => mark.type.name === 'noteEvidenceHighlight')
        ) {
          tr.removeMark(pos, pos + node.nodeSize, highlightMarkType);
          hasChanges = true;
        }

        // 2. Add new highlights
        if (!shouldHighlight || !searchTexts.length || !node.isText || !node.text) {
          return;
        }

        const textLower = node.text.toLowerCase();

        for (const searchText of searchTexts) {
          const searchLower = searchText.toLowerCase();
          if (!searchLower) continue;

          let fromIndex = 0;
          while (fromIndex < textLower.length) {
            const index = textLower.indexOf(searchLower, fromIndex);
            if (index === -1) break;

            if (highlightMarkType) {
              const mark = highlightMarkType.create();
              const from = pos + index;
              const to = from + searchText.length;
              tr.addMark(from, to, mark);
              hasMatch = true;
              hasChanges = true;
            }

            fromIndex = index + searchText.length;
          }
        }
      });

      if (hasChanges) {
        isUpdatingRef.current = true;
        tr.setMeta(NOTE_EVIDENCE_APPLY_META, true);
        view.dispatch(tr);
        isUpdatingRef.current = false;
      }

      // Update not found state
      if (shouldHighlight && searchTexts.length > 0) {
        setNoteEvidenceNotFound(!hasMatch);
      } else {
        setNoteEvidenceNotFound(false);
      }

      // Scroll the first highlight into view (only on initial apply, not on every update)
      if (!isUpdate && hasMatch) {
        setTimeout(() => {
          const highlightElement = view.dom.querySelector('.note-evidence-highlight');
          if (highlightElement) {
            highlightElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 0);
      }
    };

    // Initial application
    applyHighlights();

    // Highlights are only added while evidence is showing, and hiding it re-runs
    // this effect (the initial apply above strips them), so an edit while hidden
    // has nothing to clean up — don't subscribe, and don't walk the document.
    if (!shouldHighlight) return;

    const unsubscribe = subscribeNoteEvidenceReapply(
      editor,
      'useApplyNoteEvidence.reapply',
      () => applyHighlights(true)
    );

    return () => {
      unsubscribe();
      // Ensure any remaining highlights are cleared when the hook is cleaned up.
      // Errors during cleanup are considered non-fatal and ignored to avoid
      // surfacing noisy ProseMirror race conditions to users.
      try {
        editor.commands?.unsetMark?.('noteEvidenceHighlight');
      } catch {
        // no-op
      }
    };
  }, [editor, noteEvidence, setNoteEvidenceNotFound]);
};
